import { useEffect, useRef, useState } from 'react';
import { frameNaarSeconde, type ProjectModel } from '@video-studio/shared';
import { api, ApiFoutException, type ExportItem, type Taak } from '../api/client';

/**
 * Exportdialoog.
 *
 * De export loopt als achtergrondtaak. De voortgang komt via server-sent
 * events binnen; als die wegvalt, valt de UI terug op periodiek pollen. Zo
 * blijft de status altijd kloppen, ook achter een proxy die SSE afbreekt.
 */
export interface ExportdialoogProps {
  project: ProjectModel;
  duurInFrames: number;
  onSluiten: () => void;
  onGestart: (taak: Taak) => void;
  onKlaar: () => void;
  exports: ExportItem[];
}

export function Exportdialoog({ project, duurInFrames, onSluiten, onGestart, onKlaar, exports }: ExportdialoogProps) {
  const [kwaliteit, setKwaliteit] = useState<'snel' | 'kwaliteit'>('kwaliteit');
  const [taak, setTaak] = useState<Taak | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  const gebeurtenisBron = useRef<EventSource | null>(null);

  // frameNaarSeconde geeft al seconden terug; niet nog eens door de fps delen.
  const tweede = frameNaarSeconde(duurInFrames, project.fps);

  // Ruim de SSE-verbinding op als het dialoog sluit.
  useEffect(() => {
    return () => {
      gebeurtenisBron.current?.close();
      gebeurtenisBron.current = null;
    };
  }, []);

  const volg = (taakId: string) => {
    gebeurtenisBron.current?.close();
    const bron = new EventSource(`/v1/taken/${taakId}/stroom`);
    gebeurtenisBron.current = bron;
    bron.onmessage = (bericht) => {
      try {
        const data = JSON.parse(bericht.data) as Taak;
        setTaak(data);
        if (data.status === 'klaar') {
          bron.close();
          onKlaar();
        }
        if (data.status === 'mislukt' || data.status === 'geannuleerd') {
          bron.close();
        }
      } catch {
        // Een onleesbaar bericht negeren we; de volgende update komt zo.
      }
    };
    bron.onerror = () => {
      bron.close();
      // Terugvallen op pollen.
      const interval = window.setInterval(async () => {
        try {
          const huidig = await api.taak(taakId);
          setTaak(huidig);
          if (huidig.status === 'klaar') {
            window.clearInterval(interval);
            onKlaar();
          }
          if (huidig.status === 'mislukt' || huidig.status === 'geannuleerd') {
            window.clearInterval(interval);
          }
        } catch {
          window.clearInterval(interval);
        }
      }, 1500);
    };
  };

  const start = async () => {
    setBezig(true);
    setFout(null);
    try {
      const resultaat = await api.startExport(project.id, { kwaliteit });
      const nieuweTaak = await api.taak(resultaat.taakId);
      setTaak(nieuweTaak);
      onGestart(nieuweTaak);
      volg(resultaat.taakId);
    } catch (f) {
      setFout(f instanceof ApiFoutException ? f.message : 'De export kon niet worden gestart.');
    } finally {
      setBezig(false);
    }
  };

  const klaar = taak?.status === 'klaar';
  const mislukt = taak?.status === 'mislukt';
  const resultaat = taak?.resultaat as
    | { opslagSleutel?: string; bestandsnaam?: string; grootteBytes?: number }
    | null
    | undefined;

  return (
    <div className="dialoog__achtergrond" onClick={onSluiten}>
      <div className="dialoog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="dialoog__kop">
          <h2>Video exporteren</h2>
          <button type="button" className="knop knop--stil" onClick={onSluiten} aria-label="Sluiten">
            ✕
          </button>
        </header>

        <div className="dialoog__inhoud">
          <dl className="gegevenslijst">
            <dt>Project</dt>
            <dd>{project.naam}</dd>
            <dt>Duur</dt>
            <dd>
              {tweede.toFixed(2)} s ({duurInFrames} frames)
            </dd>
            <dt>Beeldformaat</dt>
            <dd>
              {project.breedte}×{project.hoogte} · {project.fps} fps
            </dd>
            <dt>Audio</dt>
            <dd>AAC, meegeleverd met de video</dd>
          </dl>

          <div className="veld">
            <span>Kwaliteit</span>
            <div className="keuzeknoppen">
              <button
                type="button"
                className={`knop knop--klein ${kwaliteit === 'kwaliteit' ? 'knop--actief' : ''}`}
                onClick={() => setKwaliteit('kwaliteit')}
              >
                Hoge kwaliteit (langzamer)
              </button>
              <button
                type="button"
                className={`knop knop--klein ${kwaliteit === 'snel' ? 'knop--actief' : ''}`}
                onClick={() => setKwaliteit('snel')}
              >
                Snel (tussentijds bekijken)
              </button>
            </div>
          </div>

          {fout && <div className="melding melding--fout melding--klein">{fout}</div>}

          {taak && (
            <div className="exportstatus">
              <div className="voortgangsbalk">
                <div
                  className="voortgangsbalk__vulling"
                  style={{ width: `${Math.round((taak.voortgang ?? 0) * 100)}%` }}
                />
                <span>
                  {taak.fase ?? taak.status} — {Math.round((taak.voortgang ?? 0) * 100)}%
                </span>
              </div>
              {mislukt && (
                <div className="melding melding--fout melding--klein">
                  <strong>Export mislukt.</strong> {taak.foutMelding ?? 'Onbekende fout.'}
                  <br />
                  <span className="melding__hint">
                    Herstel: controleer of alle media nog bestaat en of de clips niet langer zijn dan
                    de limiet. Probeer daarna opnieuw.
                  </span>
                </div>
              )}
              {klaar && resultaat?.bestandsnaam && (
                <div className="melding melding--goed melding--klein">
                  <strong>Klaar.</strong> {resultaat.bestandsnaam}
                  {resultaat.grootteBytes
                    ? ` — ${(resultaat.grootteBytes / (1024 * 1024)).toFixed(1)} MB`
                    : ''}
                </div>
              )}
            </div>
          )}

          {exports.length > 0 && (
            <section className="eerdereexports">
              <h3>Eerdere exports van dit project</h3>
              <ul>
                {exports.slice(0, 5).map((item) => (
                  <li key={item.id}>
                    <a href={item.downloadUrl} download>
                      {item.bestandsnaam}
                    </a>
                    <span>
                      {new Date(item.gemaaktOp).toLocaleString('nl-NL')}
                      {item.duurSeconden ? ` · ${item.duurSeconden.toFixed(2)} s` : ''}
                      {item.hoogte ? ` · ${item.hoogte}p` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <footer className="dialoog__voet">
          <button type="button" className="knop" onClick={onSluiten}>
            Sluiten
          </button>
          <button
            type="button"
            className="knop knop--primair"
            onClick={() => void start()}
            disabled={bezig || (taak !== null && !klaar && !mislukt)}
          >
            {bezig ? 'Starten…' : klaar ? 'Nog een keer exporteren' : 'Exporteren starten'}
          </button>
        </footer>
      </div>
    </div>
  );
}
